import * as THREE from 'three';
import {normalizeArcAngles} from '../../tool/normalizeArcAngles';
import {SketchCornerPointRole, SKETCH_CLOSE_TOLERANCE, SKETCH_GEOMETRY_TOLERANCE} from './SketchCornerTypes';

//------ 直线圆弧草图圆角几何解析 开始------
/**
 * 直线—圆弧草图圆角几何解析器。
 *
 * 职责：
 * 1. 解析真实共享角点或延长后的虚拟交点；
 * 2. 枚举直线偏移线与圆弧同心候选圆的交点；
 * 3. 根据选择命中位置、可修剪范围和源圆弧方向选择唯一候选；
 * 4. 只返回不可变更模型的几何结果，供预览和正式提交共同使用。
 */
export class SketchLineArcFilletGeometryResolver implements SketchLineArcFilletGeometryResolverLike {
    /** 完整圆周弧度。 */
    private static readonly FULL_SWEEP = Math.PI * 2;

    /** 角度比较容差。 */
    private static readonly ANGLE_TOLERANCE = 1e-7;

    /**
     * 解析直线—圆弧圆角几何。
     * @param input 解析输入。
     * @returns 最符合选择意图的圆角几何；不存在有效解时返回 null。
     */
    public resolve(input: SketchLineArcFilletResolveInput): SketchLineArcFilletGeometryResult | null {
        const {lineSelection, arcSelection, radius} = input;
        const line = lineSelection.object;
        const sourceArc = arcSelection.object;
        if (!Number.isFinite(radius) || radius <= SKETCH_GEOMETRY_TOLERANCE) {
            return null;
        }
        if (line.referencePlane !== sourceArc.referencePlane) {
            return null;
        }

        const lineStartLocal = line.referencePlane.worldToLocal(line.startPoint.getPosition());
        const lineEndLocal = line.referencePlane.worldToLocal(line.endPoint.getPosition());
        const lineVector = lineEndLocal.clone().sub(lineStartLocal);
        const lineLength = lineVector.length();
        if (!Number.isFinite(lineLength) || lineLength <= SKETCH_GEOMETRY_TOLERANCE) {
            return null;
        }
        const lineDirection = lineVector.multiplyScalar(1 / lineLength);

        const sourceArcDefinition = sourceArc.getArcDefinition();
        if (
            !Number.isFinite(sourceArcDefinition.radius)
            || sourceArcDefinition.radius <= SKETCH_GEOMETRY_TOLERANCE
        ) {
            return null;
        }
        const sourceCenterLocal = sourceArc.referencePlane.worldToLocal(sourceArcDefinition.centerWorld);
        const lineHitLocal = this.resolveSelectionHitLocal(
            lineSelection,
            line.startPoint.getPosition().add(line.endPoint.getPosition()).multiplyScalar(0.5),
        );
        const arcHitLocal = this.resolveSelectionHitLocal(
            arcSelection,
            sourceArc.getMidpointPosition(),
        );
        const cornerContext = this.resolveCornerContext(
            input,
            lineStartLocal,
            lineEndLocal,
            lineDirection,
            sourceCenterLocal,
            sourceArcDefinition.radius,
            lineHitLocal,
            arcHitLocal,
        );
        if (!cornerContext) {
            return null;
        }

        const lineNormal = new THREE.Vector2(-lineDirection.y, lineDirection.x);
        const candidates: SketchLineArcFilletCandidate[] = [];
        const centerDistanceCandidates = [{
            centerDistance: sourceArcDefinition.radius + radius,
            sourceTangentDirection: 1,
        }];
        const internalCenterDistance = Math.abs(sourceArcDefinition.radius - radius);
        if (internalCenterDistance > SKETCH_GEOMETRY_TOLERANCE) {
            centerDistanceCandidates.push({
                centerDistance: internalCenterDistance,
                sourceTangentDirection: sourceArcDefinition.radius >= radius ? 1 : -1,
            });
        }

        /**
         * 圆角圆心必须同时位于“距直线 r 的两条偏移线”与
         * “距源圆弧圆心 R+r 或 |R-r| 的同心圆”上，因此只需枚举有限候选。
         */
        [-1, 1].forEach((lineSide) => {
            const offsetLineStart = lineStartLocal.clone().addScaledVector(lineNormal, lineSide * radius);
            centerDistanceCandidates.forEach((tangencyMode) => {
                const centerCandidates = this.resolveLineCircleIntersections(
                    offsetLineStart,
                    lineDirection,
                    sourceCenterLocal,
                    tangencyMode.centerDistance,
                );
                centerCandidates.forEach((centerLocal) => {
                    const sourceCenterDirection = centerLocal.clone().sub(sourceCenterLocal);
                    if (sourceCenterDirection.lengthSq() <= SKETCH_GEOMETRY_TOLERANCE) {
                        return;
                    }
                    sourceCenterDirection.normalize();

                    const lineTangentLocal = centerLocal.clone().addScaledVector(
                        lineNormal,
                        -lineSide * radius,
                    );
                    const arcTangentLocal = sourceCenterLocal.clone().addScaledVector(
                        sourceCenterDirection,
                        sourceArcDefinition.radius * tangencyMode.sourceTangentDirection,
                    );
                    const candidate = this.buildCandidate({
                        input,
                        cornerContext,
                        sourceArcDefinition,
                        sourceCenterLocal,
                        lineHitLocal,
                        arcHitLocal,
                        lineTangentLocal,
                        arcTangentLocal,
                        centerLocal,
                    });
                    if (candidate) {
                        candidates.push(candidate);
                    }
                });
            });
        });

        if (candidates.length <= 0) {
            return null;
        }
        candidates.sort((first, second) => first.score - second.score);
        const bestCandidate = candidates[0];
        const referencePlane = line.referencePlane;
        const arcPoints = this.buildArcWorldPoints(
            referencePlane,
            bestCandidate.centerLocal,
            radius,
            bestCandidate.filletStartAngle,
            bestCandidate.filletSweep,
            bestCandidate.filletClockwise,
        );
        if (arcPoints.length < 2) {
            return null;
        }

        return {
            ...bestCandidate,
            line,
            sourceArc,
            lineCornerRole: cornerContext.lineCornerRole,
            arcCornerRole: cornerContext.arcCornerRole,
            lineOldCornerPoint: cornerContext.lineOldCornerPoint,
            arcOldCornerPoint: cornerContext.arcOldCornerPoint,
            radius,
            lineTangentWorld: referencePlane.localToWorld(bestCandidate.lineTangentLocal),
            arcTangentWorld: referencePlane.localToWorld(bestCandidate.arcTangentLocal),
            centerWorld: referencePlane.localToWorld(bestCandidate.centerLocal),
            arcPoints,
        };
    }

    /**
     * 解析源角点、替换端点角色与保留侧。
     * @param input 解析输入。
     * @param lineStartLocal 直线起点局部坐标。
     * @param lineEndLocal 直线终点局部坐标。
     * @param lineDirection 直线局部单位方向。
     * @param sourceCenterLocal 源圆弧圆心局部坐标。
     * @param sourceRadius 源圆弧半径。
     * @param lineHitLocal 直线命中局部坐标。
     * @param arcHitLocal 圆弧命中局部坐标。
     * @returns 角点上下文；无法确定时返回 null。
     */
    private resolveCornerContext(
        input: SketchLineArcFilletResolveInput,
        lineStartLocal: THREE.Vector2,
        lineEndLocal: THREE.Vector2,
        lineDirection: THREE.Vector2,
        sourceCenterLocal: THREE.Vector2,
        sourceRadius: number,
        lineHitLocal: THREE.Vector2,
        arcHitLocal: THREE.Vector2,
    ): SketchLineArcFilletCornerContext | null {
        const line = input.lineSelection.object;
        const sourceArc = input.arcSelection.object;
        const endpointPairs = [
            {
                linePoint: line.startPoint,
                arcPoint: sourceArc.getStartPoint(),
                lineRole: SketchCornerPointRole.Start,
                arcRole: SketchCornerPointRole.Start,
            },
            {
                linePoint: line.startPoint,
                arcPoint: sourceArc.getEndPoint(),
                lineRole: SketchCornerPointRole.Start,
                arcRole: SketchCornerPointRole.Terminal,
            },
            {
                linePoint: line.endPoint,
                arcPoint: sourceArc.getStartPoint(),
                lineRole: SketchCornerPointRole.Terminal,
                arcRole: SketchCornerPointRole.Start,
            },
            {
                linePoint: line.endPoint,
                arcPoint: sourceArc.getEndPoint(),
                lineRole: SketchCornerPointRole.Terminal,
                arcRole: SketchCornerPointRole.Terminal,
            },
        ];

        const sharedPair = endpointPairs.find((pair) => (
            pair.linePoint === pair.arcPoint
            || pair.linePoint.getPosition().distanceTo(pair.arcPoint.getPosition()) <= SKETCH_CLOSE_TOLERANCE
        ));
        if (sharedPair) {
            const cornerWorld = sharedPair.linePoint.getPosition()
                .add(sharedPair.arcPoint.getPosition())
                .multiplyScalar(0.5);
            return {
                cornerLocal: line.referencePlane.worldToLocal(cornerWorld),
                isSharedCorner: true,
                lineCornerRole: sharedPair.lineRole,
                arcCornerRole: sharedPair.arcRole,
                lineFarLocal: sharedPair.lineRole === SketchCornerPointRole.Start
                    ? lineEndLocal.clone()
                    : lineStartLocal.clone(),
                lineOldCornerPoint: sharedPair.linePoint,
                arcOldCornerPoint: sharedPair.arcPoint,
            };
        }

        /**
         * 非相交实体按 SolidWorks 的虚拟交点语义处理：
         * 直线使用无限延长线，圆弧使用其支撑圆，并用两次鼠标命中共同选择交点分支。
         */
        const supportIntersections = this.resolveLineCircleIntersections(
            lineStartLocal,
            lineDirection,
            sourceCenterLocal,
            sourceRadius,
        );
        if (supportIntersections.length <= 0) {
            return null;
        }
        supportIntersections.sort((first, second) => (
            first.distanceTo(lineHitLocal) + first.distanceTo(arcHitLocal)
            - second.distanceTo(lineHitLocal) - second.distanceTo(arcHitLocal)
        ));
        const cornerLocal = supportIntersections[0];
        const lineCornerRole = lineStartLocal.distanceToSquared(cornerLocal)
            <= lineEndLocal.distanceToSquared(cornerLocal)
            ? SketchCornerPointRole.Start
            : SketchCornerPointRole.Terminal;
        const arcStartLocal = sourceArc.referencePlane.worldToLocal(sourceArc.getStartPoint().getPosition());
        const arcEndLocal = sourceArc.referencePlane.worldToLocal(sourceArc.getEndPoint().getPosition());
        const arcCornerRole = arcStartLocal.distanceToSquared(cornerLocal)
            <= arcEndLocal.distanceToSquared(cornerLocal)
            ? SketchCornerPointRole.Start
            : SketchCornerPointRole.Terminal;

        return {
            cornerLocal,
            isSharedCorner: false,
            lineCornerRole,
            arcCornerRole,
            lineFarLocal: lineCornerRole === SketchCornerPointRole.Start
                ? lineEndLocal.clone()
                : lineStartLocal.clone(),
            lineOldCornerPoint: lineCornerRole === SketchCornerPointRole.Start
                ? line.startPoint
                : line.endPoint,
            arcOldCornerPoint: arcCornerRole === SketchCornerPointRole.Start
                ? sourceArc.getStartPoint()
                : sourceArc.getEndPoint(),
        };
    }

    /**
     * 构建并校验单个候选解。
     * @param options 候选输入。
     * @returns 有效候选；无法保留源实体时返回 null。
     */
    private buildCandidate(options: {
        input: SketchLineArcFilletResolveInput;
        cornerContext: SketchLineArcFilletCornerContext;
        sourceArcDefinition: Required<import('../../mesh/sketch').SketchArcDefinition>;
        sourceCenterLocal: THREE.Vector2;
        lineHitLocal: THREE.Vector2;
        arcHitLocal: THREE.Vector2;
        lineTangentLocal: THREE.Vector2;
        arcTangentLocal: THREE.Vector2;
        centerLocal: THREE.Vector2;
    }): SketchLineArcFilletCandidate | null {
        const {
            input,
            cornerContext,
            sourceArcDefinition,
            sourceCenterLocal,
            lineHitLocal,
            arcHitLocal,
            lineTangentLocal,
            arcTangentLocal,
            centerLocal,
        } = options;
        const retainedLineVector = cornerContext.lineFarLocal.clone().sub(lineTangentLocal);
        if (retainedLineVector.length() <= SKETCH_GEOMETRY_TOLERANCE) {
            return null;
        }

        const cornerToFar = cornerContext.lineFarLocal.clone().sub(cornerContext.cornerLocal);
        const farDistance = cornerToFar.length();
        if (farDistance <= SKETCH_GEOMETRY_TOLERANCE) {
            return null;
        }
        cornerToFar.multiplyScalar(1 / farDistance);
        const lineTrimProgress = lineTangentLocal.clone().sub(cornerContext.cornerLocal).dot(cornerToFar);
        if (lineTrimProgress <= SKETCH_GEOMETRY_TOLERANCE) {
            return null;
        }
        if (
            cornerContext.isSharedCorner
            && lineTrimProgress >= farDistance - SKETCH_GEOMETRY_TOLERANCE
        ) {
            return null;
        }

        const nextSourceArcDefinition = this.buildNextSourceArcDefinition(
            sourceArcDefinition,
            cornerContext.arcCornerRole,
            arcTangentLocal,
            sourceCenterLocal,
            cornerContext.isSharedCorner,
        );
        if (!nextSourceArcDefinition) {
            return null;
        }

        const filletArc = this.resolveMinorFilletArc(
            centerLocal,
            lineTangentLocal,
            arcTangentLocal,
        );
        if (!filletArc) {
            return null;
        }

        const sourceOriginalSweep = normalizeArcAngles(
            sourceArcDefinition.startAngle,
            sourceArcDefinition.endAngle,
            sourceArcDefinition.clockwise,
        ).angleRange;
        const sourceNextSweep = normalizeArcAngles(
            nextSourceArcDefinition.startAngle,
            nextSourceArcDefinition.endAngle,
            nextSourceArcDefinition.clockwise,
        ).angleRange;
        const lineHitDistance = this.resolvePointToSegmentDistance(
            lineHitLocal,
            cornerContext.lineFarLocal,
            lineTangentLocal,
        );
        const arcHitAngle = Math.atan2(
            arcHitLocal.y - sourceCenterLocal.y,
            arcHitLocal.x - sourceCenterLocal.x,
        );
        const arcHitDistance = this.resolveAngleDistanceToArc(
            arcHitAngle,
            nextSourceArcDefinition,
        ) * sourceArcDefinition.radius;

        /**
         * 评分以“靠近用户选择的角点”为主，命中保留侧和源圆弧扫掠变化只作为次级判据。
         * 这样用户即使点在即将被修剪的小段上，也不会被错误判为无解。
         */
        const score = lineTangentLocal.distanceTo(cornerContext.cornerLocal)
            + arcTangentLocal.distanceTo(cornerContext.cornerLocal)
            + lineHitDistance * 0.05
            + arcHitDistance * 0.05
            + Math.abs(sourceNextSweep - sourceOriginalSweep) * sourceArcDefinition.radius * 0.01;

        return {
            lineTangentLocal,
            arcTangentLocal,
            centerLocal,
            nextSourceArcDefinition,
            filletStartAngle: filletArc.start,
            filletEndAngle: filletArc.end,
            filletSweep: filletArc.angleRange,
            filletClockwise: filletArc.clockwise,
            score,
        };
    }

    /**
     * 构建修剪或延伸后的源圆弧定义。
     * @param sourceDefinition 源圆弧定义。
     * @param cornerRole 被替换端点角色。
     * @param tangentLocal 新切点局部坐标。
     * @param centerLocal 源圆弧圆心局部坐标。
     * @param requireTrimInsideOriginalArc 共享角点是否要求严格在原弧内部修剪。
     * @returns 新定义；会退化或改变错误分支时返回 null。
     */
    private buildNextSourceArcDefinition(
        sourceDefinition: Required<import('../../mesh/sketch').SketchArcDefinition>,
        cornerRole: SketchCornerPointRole,
        tangentLocal: THREE.Vector2,
        centerLocal: THREE.Vector2,
        requireTrimInsideOriginalArc: boolean,
    ): Required<import('../../mesh/sketch').SketchArcDefinition> | null {
        const tangentAngle = Math.atan2(
            tangentLocal.y - centerLocal.y,
            tangentLocal.x - centerLocal.x,
        );
        const normalizedSource = normalizeArcAngles(
            sourceDefinition.startAngle,
            sourceDefinition.endAngle,
            sourceDefinition.clockwise,
        );
        const tangentProgress = this.resolveDirectedAngleDistance(
            normalizedSource.start,
            tangentAngle,
            sourceDefinition.clockwise,
        );

        let nextStart = normalizedSource.start;
        let nextSweep = normalizedSource.angleRange;
        if (requireTrimInsideOriginalArc) {
            if (
                tangentProgress <= SketchLineArcFilletGeometryResolver.ANGLE_TOLERANCE
                || tangentProgress >= normalizedSource.angleRange
                    - SketchLineArcFilletGeometryResolver.ANGLE_TOLERANCE
            ) {
                return null;
            }
            if (cornerRole === SketchCornerPointRole.Start) {
                nextStart = sourceDefinition.clockwise
                    ? normalizedSource.start - tangentProgress
                    : normalizedSource.start + tangentProgress;
                nextSweep = normalizedSource.angleRange - tangentProgress;
            } else {
                nextSweep = tangentProgress;
            }
        } else if (cornerRole === SketchCornerPointRole.Start) {
            nextStart = tangentAngle;
            nextSweep = this.resolveDirectedAngleDistance(
                nextStart,
                normalizedSource.end,
                sourceDefinition.clockwise,
            );
        } else {
            nextSweep = this.resolveDirectedAngleDistance(
                normalizedSource.start,
                tangentAngle,
                sourceDefinition.clockwise,
            );
        }

        if (
            nextSweep <= SketchLineArcFilletGeometryResolver.ANGLE_TOLERANCE
            || nextSweep >= SketchLineArcFilletGeometryResolver.FULL_SWEEP
                - SketchLineArcFilletGeometryResolver.ANGLE_TOLERANCE
        ) {
            return null;
        }
        const nextEnd = sourceDefinition.clockwise
            ? nextStart - nextSweep
            : nextStart + nextSweep;
        return {
            ...sourceDefinition,
            centerWorld: sourceDefinition.centerWorld.clone(),
            startAngle: nextStart,
            endAngle: nextEnd,
        };
    }

    /**
     * 解析新圆角起止角，固定使用不超过半圆的短弧。
     * @param centerLocal 圆角圆心。
     * @param startLocal 直线切点。
     * @param endLocal 圆弧切点。
     * @returns 圆角角度定义；端点重合时返回 null。
     */
    private resolveMinorFilletArc(
        centerLocal: THREE.Vector2,
        startLocal: THREE.Vector2,
        endLocal: THREE.Vector2,
    ): {start: number; end: number; angleRange: number; clockwise: boolean} | null {
        const start = Math.atan2(startLocal.y - centerLocal.y, startLocal.x - centerLocal.x);
        const rawEnd = Math.atan2(endLocal.y - centerLocal.y, endLocal.x - centerLocal.x);
        const counterClockwiseSweep = this.normalizePositiveAngle(rawEnd - start);
        const clockwiseSweep = this.normalizePositiveAngle(start - rawEnd);
        const clockwise = clockwiseSweep < counterClockwiseSweep;
        const angleRange = clockwise ? clockwiseSweep : counterClockwiseSweep;
        if (
            angleRange <= SketchLineArcFilletGeometryResolver.ANGLE_TOLERANCE
            || angleRange > Math.PI + SketchLineArcFilletGeometryResolver.ANGLE_TOLERANCE
        ) {
            return null;
        }
        return {
            start,
            end: clockwise ? start - angleRange : start + angleRange,
            angleRange,
            clockwise,
        };
    }

    /**
     * 求无限直线与圆的交点。
     * @param lineStart 直线上任一点。
     * @param lineDirection 直线单位方向。
     * @param circleCenter 圆心。
     * @param radius 圆半径。
     * @returns 零到两个交点。
     */
    private resolveLineCircleIntersections(
        lineStart: THREE.Vector2,
        lineDirection: THREE.Vector2,
        circleCenter: THREE.Vector2,
        radius: number,
    ): THREE.Vector2[] {
        if (!Number.isFinite(radius) || radius <= SKETCH_GEOMETRY_TOLERANCE) {
            return [];
        }
        const centerDelta = circleCenter.clone().sub(lineStart);
        const projection = centerDelta.dot(lineDirection);
        const closestPoint = lineStart.clone().addScaledVector(lineDirection, projection);
        const closestDistanceSquared = closestPoint.distanceToSquared(circleCenter);
        const radiusSquared = radius * radius;
        if (closestDistanceSquared > radiusSquared + SKETCH_GEOMETRY_TOLERANCE) {
            return [];
        }
        const halfChord = Math.sqrt(Math.max(radiusSquared - closestDistanceSquared, 0));
        if (halfChord <= SKETCH_GEOMETRY_TOLERANCE) {
            return [closestPoint];
        }
        return [
            closestPoint.clone().addScaledVector(lineDirection, halfChord),
            closestPoint.clone().addScaledVector(lineDirection, -halfChord),
        ];
    }

    /**
     * 将选择命中点转换到参考面局部坐标。
     * @param selection 选择信息。
     * @param fallbackWorld 缺少命中点时的世界坐标回退值。
     * @returns 局部坐标。
     */
    private resolveSelectionHitLocal(
        selection: SketchCornerSourceSelection,
        fallbackWorld: THREE.Vector3,
    ): THREE.Vector2 {
        const hitPoint = selection.hitPoint;
        const world = hitPoint && hitPoint.every((value) => Number.isFinite(value))
            ? new THREE.Vector3(hitPoint[0], hitPoint[1], hitPoint[2])
            : fallbackWorld;
        return selection.object.referencePlane.worldToLocal(world);
    }

    /**
     * 计算指定方向上的正角度距离。
     * @param start 起始角。
     * @param end 终止角。
     * @param clockwise 是否顺时针。
     * @returns `[0, 2π)` 内的角度距离。
     */
    private resolveDirectedAngleDistance(start: number, end: number, clockwise: boolean): number {
        return this.normalizePositiveAngle(clockwise ? start - end : end - start);
    }

    /**
     * 将角度标准化到 `[0, 2π)`。
     * @param angle 输入角度。
     * @returns 标准化角度。
     */
    private normalizePositiveAngle(angle: number): number {
        const normalized = angle % SketchLineArcFilletGeometryResolver.FULL_SWEEP;
        return normalized < 0
            ? normalized + SketchLineArcFilletGeometryResolver.FULL_SWEEP
            : normalized;
    }

    /**
     * 计算点到线段的最短距离。
     * @param point 目标点。
     * @param segmentStart 线段起点。
     * @param segmentEnd 线段终点。
     * @returns 最短距离。
     */
    private resolvePointToSegmentDistance(
        point: THREE.Vector2,
        segmentStart: THREE.Vector2,
        segmentEnd: THREE.Vector2,
    ): number {
        const segment = segmentEnd.clone().sub(segmentStart);
        const lengthSquared = segment.lengthSq();
        if (lengthSquared <= SKETCH_GEOMETRY_TOLERANCE) {
            return point.distanceTo(segmentStart);
        }
        const factor = THREE.MathUtils.clamp(
            point.clone().sub(segmentStart).dot(segment) / lengthSquared,
            0,
            1,
        );
        return point.distanceTo(segmentStart.clone().addScaledVector(segment, factor));
    }

    /**
     * 计算角度到圆弧范围的最短角距离。
     * @param angle 目标角度。
     * @param definition 圆弧定义。
     * @returns 角度已在圆弧内时返回 0，否则返回到最近端点的角距离。
     */
    private resolveAngleDistanceToArc(
        angle: number,
        definition: Required<import('../../mesh/sketch').SketchArcDefinition>,
    ): number {
        const normalized = normalizeArcAngles(
            definition.startAngle,
            definition.endAngle,
            definition.clockwise,
        );
        const progress = this.resolveDirectedAngleDistance(
            normalized.start,
            angle,
            definition.clockwise,
        );
        if (progress <= normalized.angleRange + SketchLineArcFilletGeometryResolver.ANGLE_TOLERANCE) {
            return 0;
        }
        return Math.min(
            this.normalizePositiveAngle(angle - normalized.start),
            this.normalizePositiveAngle(normalized.start - angle),
            this.normalizePositiveAngle(angle - normalized.end),
            this.normalizePositiveAngle(normalized.end - angle),
        );
    }

    /**
     * 构建圆角世界采样点。
     * @param referencePlane 参考面。
     * @param centerLocal 圆心局部坐标。
     * @param radius 半径。
     * @param startAngle 起始角。
     * @param sweep 扫掠角。
     * @param clockwise 是否顺时针。
     * @returns 世界采样点。
     */
    private buildArcWorldPoints(
        referencePlane: SketchLineArcFilletResolveInput['lineSelection']['object']['referencePlane'],
        centerLocal: THREE.Vector2,
        radius: number,
        startAngle: number,
        sweep: number,
        clockwise: boolean,
    ): THREE.Vector3[] {
        const steps = Math.max(16, Math.ceil(64 * sweep / SketchLineArcFilletGeometryResolver.FULL_SWEEP));
        const points: THREE.Vector3[] = [];
        for (let index = 0; index <= steps; index += 1) {
            const progress = index / steps;
            const angle = clockwise
                ? startAngle - sweep * progress
                : startAngle + sweep * progress;
            points.push(referencePlane.localToWorld(new THREE.Vector2(
                centerLocal.x + radius * Math.cos(angle),
                centerLocal.y + radius * Math.sin(angle),
            )));
        }
        return points;
    }
}
//------ 直线圆弧草图圆角几何解析 结束------
